import { Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

export interface DueDateLabel {
  overdue: boolean;
  date: string;
}

/**
 * Formats a task due date as a short absolute date in the portal language ("May 5", or "May 5, 2025"
 * outside the current year) and flags it as overdue once the due day has passed.
 * Impure so it re-evaluates on language change.
 */
@Pipe({ name: 'dueDate', pure: false, standalone: true })
export class DueDatePipe implements PipeTransform {
  private _formatters = new Map<string, Intl.DateTimeFormat>();

  constructor(private readonly _translocoService: TranslocoService) {}

  transform(value: string | Date | null | undefined): DueDateLabel | null {
    if (!value) {
      return null;
    }
    const due = new Date(value);
    if (isNaN(due.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);

    const lang = this._translocoService.getActiveLang() || 'en';
    const withYear = due.getFullYear() !== today.getFullYear();
    return {
      overdue: dueDay < today,
      date: this._formatter(lang, withYear).format(due),
    };
  }

  private _formatter(lang: string, withYear: boolean): Intl.DateTimeFormat {
    const key = `${lang}|${withYear}`;
    let formatter = this._formatters.get(key);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(lang, withYear
        ? { month: 'short', day: 'numeric', year: 'numeric' }
        : { month: 'short', day: 'numeric' });
      this._formatters.set(key, formatter);
    }
    return formatter;
  }
}
